'use client';

import { Form, Formik } from 'formik';
import {
    Button,
    Drawer,
    Grid,
    Heading,
    TextField,
    TextPassage,
} from '@petsmart-ui/sparky';

import {
    AccountDrawerType,
    getNextApiRoute,
    GoogleRecaptchaCheckbox,
    logSentryError,
    recaptchaId,
    SentryAcpForgotPasswordErrors as SentryErrors,
    typedFetch,
    useGoogleRecaptcha,
    usePageLayout,
} from '@libs/common-next';
import { HttpMethod, isLocaleCA } from '@libs/core';

import type {
    IdentityResetPasswordResponse,
    IdentityRouteResponse,
} from '@libs/experience-api';
import { useEffect, useState } from 'react';
import { trackResetPassword } from '@libs/analytics';
import type { ForgotPasswordForm } from './type';
import {
    trackForgotPasswordFormErrors,
    validateForgotPassword,
} from './schema';

import { ResetPasswordConfirmationDrawer } from '../ResetPasswordConfirmationDrawer';

import './ForgotPasswordDrawer.scss';
import { ValidationAlerts } from '../ValidationAlert';

// [AC-3241] Tech Debt, resolving circular dependency
const TEST_ENVIRONMENTS = new Set([
    'localhost',
    'test.pilot.petsmart.com',
    'test.pilot.petsmart.ca',
    'staging.pilot.petsmart.com',
    'staging.pilot.petsmart.ca',
]);

const initialFormValues: ForgotPasswordForm = {
    email: '',
};

export type ForgotPasswordResponse =
    IdentityRouteResponse<IdentityResetPasswordResponse | null>;

export function ForgotPasswordDrawer() {
    const [isFetching, setIsFetching] = useState(false);
    const [showAlert, setShowAlert] = useState({
        status: false,
        showWentWrong: false,
    });

    const {
        handleRecaptcha,
        useRecaptchaCheckbox,
        setUseRecaptchaCheckbox,
        resetRecaptchaCheckbox,
    } = useGoogleRecaptcha();
    const {
        accountDrawersOpen,
        removeAccountDrawers,
        openAccountDrawer,
        pageType,
    } = usePageLayout();

    const checkboxId = recaptchaId.forgotPasswordCheckbox;

    useEffect(() => {
        setShowAlert({ status: false, showWentWrong: false });
    }, []);

    async function handleResetPassword({ email }: ForgotPasswordForm) {
        if (isFetching) return;

        setIsFetching(true);
        setShowAlert({ status: false, showWentWrong: false });

        try {
            const resultRecaptcha = await handleRecaptcha(
                recaptchaId.forgotPasswordScore
            );

            if (!resultRecaptcha) {
                setIsFetching(false);
                return;
            }

            const isTestEnv = TEST_ENVIRONMENTS.has(window.location.hostname);
            const tld = isLocaleCA() ? 'ca' : 'com';
            const baseOrigin = isTestEnv
                ? `https://development.pilot.petsmart.${tld}`
                : window.location.origin;
            const redirectUri = `${baseOrigin}/account/?screenToRender=resetPasswordRequestCode`;

            const resResetPassword = await typedFetch<ForgotPasswordResponse>(
                getNextApiRoute('identity/resetPassword'),
                {
                    method: HttpMethod.POST,
                    body: JSON.stringify({ email, redirectUri }),
                    additionalSuccessCodes: [400, 401, 500],
                }
            );

            if (resResetPassword?.success) {
                setUseRecaptchaCheckbox(null);
                trackResetPassword();
            } else {
                if (
                    resResetPassword?.data &&
                    'errorCode' in resResetPassword.data &&
                    (resResetPassword.data.errorCode === 390 ||
                        resResetPassword.data.errorCode === 319)
                ) {
                    setShowAlert({ status: true, showWentWrong: false });
                    throw Error;
                }

                if (
                    resResetPassword?.data &&
                    'score' in resResetPassword.data &&
                    resResetPassword.data.score
                ) {
                    if (
                        'reasons' in resResetPassword.data &&
                        resResetPassword.data.reasons?.includes(
                            'TOO_MANY_ATTEMPTS'
                        )
                    ) {
                        setShowAlert({ status: true, showWentWrong: true });
                    }

                    if (useRecaptchaCheckbox) {
                        resetRecaptchaCheckbox('failed');
                    } else {
                        setUseRecaptchaCheckbox(checkboxId);
                    }

                    throw Error;
                }
            }

            openAccountDrawer(AccountDrawerType.RESET_PASSWORD_CONFIRMATION);
        } catch (err) {
            logSentryError(err, SentryErrors.ForgotPassword.SubmitFailed);
        } finally {
            setIsFetching(false);
        }
    }

    return (
        <Formik
            onSubmit={handleResetPassword}
            initialValues={initialFormValues}
            validate={validateForgotPassword}
            validateOnBlur
        >
            {({
                errors,
                values,
                handleChange,
                handleBlur,
                touched,
                resetForm,
            }) => (
                <>
                    <Drawer
                        data-testid="forgot-password-drawer"
                        align="right"
                        isActive={accountDrawersOpen?.includes(
                            AccountDrawerType.FORGOT_PASSWORD
                        )}
                        onClose={() => {
                            removeAccountDrawers([
                                AccountDrawerType.SIGN_IN,
                                AccountDrawerType.FORGOT_PASSWORD,
                            ]);
                            setShowAlert({
                                status: false,
                                showWentWrong: false,
                            });
                            resetForm();
                        }}
                        ariaLabelledBy="drawer-heading-1"
                        className="forgot-password-drawer"
                    >
                        <Drawer.Header>
                            <Heading
                                data-testid="forgot-password-drawer-heading-1"
                                id="drawer-heading-1"
                                tagName="h5"
                                size="title"
                            >
                                Forgot password
                            </Heading>
                        </Drawer.Header>
                        <Drawer.Body>
                            <Form noValidate>
                                <Grid>
                                    <Grid.Item>
                                        {showAlert.status && (
                                            <ValidationAlerts
                                                defaultHeading={
                                                    showAlert.showWentWrong
                                                        ? 'Something went wrong'
                                                        : 'Too many attempts'
                                                }
                                                validationErrors={[
                                                    {
                                                        message:
                                                            showAlert.showWentWrong
                                                                ? 'Please try again.'
                                                                : 'Please wait 1-2 minutes and try again.',
                                                    },
                                                ]}
                                            />
                                        )}
                                        <TextPassage>
                                            Enter the email address associated
                                            with your Treats Rewards account and
                                            we will send you a link to reset
                                            your password.
                                        </TextPassage>
                                    </Grid.Item>
                                    <Grid.Item>
                                        <TextField
                                            data-testid="forgot-drawer-email"
                                            data-cs-mask
                                            id="forgot-drawer-email"
                                            label="Email address"
                                            type="email"
                                            name="email"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            isError={
                                                touched.email && !!errors.email
                                            }
                                            fieldNote={
                                                touched.email
                                                    ? errors.email
                                                    : ''
                                            }
                                            value={values.email}
                                        />
                                    </Grid.Item>
                                    {useRecaptchaCheckbox && (
                                        <Grid.Item>
                                            <GoogleRecaptchaCheckbox
                                                id={checkboxId}
                                                page={pageType}
                                                parent="ForgotPasswordDrawer"
                                            />
                                        </Grid.Item>
                                    )}
                                    <Grid.Item>
                                        <Button
                                            data-testid="reset-password-btn"
                                            hideText={isFetching}
                                            fullWidth
                                            loading={isFetching}
                                            size="lg"
                                            text="Reset password"
                                            variant="primary"
                                            onClick={() =>
                                                // Error tracking for Data Layer should fire on click
                                                // because form validation runs onChange and spams Data Layer
                                                trackForgotPasswordFormErrors(
                                                    values
                                                )
                                            }
                                        />
                                    </Grid.Item>
                                </Grid>
                            </Form>
                        </Drawer.Body>
                    </Drawer>
                    <ResetPasswordConfirmationDrawer email={values.email} />
                </>
            )}
        </Formik>
    );
}
